import { makeAutoObservable, runInAction, reaction, computed } from 'mobx'
import {
  getFirestore,
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore'
import { getCollection } from '../../../shared/api/firestore'
import FirebaseApp from '../../../shared/api/firebase'
import {
  uploadServiceImage,
  deleteImageByUrl,
} from '../../../shared/api/storage'

const db = getFirestore(FirebaseApp)

class ServiceStore {
  services = []
  isLoading = false
  isUploading = false
  selectedCategory = 'all'
  searchTerm = ''

  constructor(rootStore) {
    makeAutoObservable(
      this,
      {
        rootStore: false,
        sortedServices: computed,
        categories: computed,
        minServiceDuration: computed,
        servicesById: computed,
      },
      { autoBind: true },
    )
    this.rootStore = rootStore
    this.organizationStore = rootStore.organizationStore
    this.errorStore = rootStore.errorStore
    this.staffStore = rootStore.staffStore // Добавляем staffStore
    this.sortStore = rootStore.sortStore

    // Реакция для автоматической загрузки услуг при изменении ID организации
    reaction(
      () => this.organizationStore.organization?.id,
      (orgId) => {
        if (orgId) {
          this.fetchServices(orgId)
        } else {
          // Очищаем услуги, если организации нет
          runInAction(() => {
            this.services = []
          })
        }
      },
      { fireImmediately: true }, // Запускаем сразу, чтобы поймать начальное состояние
    )
  }

  async fetchServices(organizationId) {
    if (!organizationId) return
    this.isLoading = true
    try {
      const servicesList = await getCollection([
        'organizations',
        organizationId,
        'services',
      ])
      runInAction(() => {
        // Сортируем на клиенте по полю order.
        // Услуги без `order` окажутся в конце.
        this.services = servicesList.sort(
          (a, b) => (a.order ?? Infinity) - (b.order ?? Infinity),
        )
      })
    } catch (error) {
      console.error('Не удалось загрузить услуги:', error)
      this.errorStore.showError('Не удалось загрузить список услуг')
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  async addService(serviceData) {
    const orgId = this.organizationStore.organization.id
    if (!orgId) {
      this.errorStore.showError('Организация не найдена.')
      return null
    }

    this.isLoading = true
    try {
      const serviceCollectionRef = collection(
        db,
        'organizations',
        orgId,
        'services',
      )
      const newDocRef = doc(serviceCollectionRef) // Auto-generates ID

      // Новая услуга добавляется в конец списка
      let newServiceData = {
        ...serviceData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        order: this.services.length, // Присваиваем порядковый номер
      }

      // Если режим "соло", автоматически привязываем услугу к владельцу.
      if (this.organizationStore.isSoloMode) {
        // ID сотрудника-владельца теперь получаем из staffStore
        const ownerStaffId = this.staffStore.ownerStaffId
        if (ownerStaffId) {
          // staffIds - это массив ID сотрудников, которые предоставляют эту услугу.
          newServiceData.staffIds = [ownerStaffId]
        } else {
          // Это крайний случай, если документ сотрудника-владельца еще не загружен.
          // Лучше прервать операцию и сообщить об ошибке.
          this.errorStore.showError(
            'Не удалось найти сотрудника-владельца. Попробуйте перезагрузить страницу.',
          )
          return null
        }
      }

      await setDoc(newDocRef, newServiceData)

      // Optimistic update of local state
      runInAction(() => {
        const optimisticService = {
          ...newServiceData, // Используем полный объект данных
          id: newDocRef.id,
          createdAt: new Date(), // Заменяем serverTimestamp на локальную дату для немедленного отображения
          updatedAt: new Date(),
        }
        // Иммутабельное обновление: создаем новый массив, добавляя в него новый элемент.
        // Это гарантирует, что MobX обнаружит изменение.
        this.services = [...this.services, optimisticService] // Correct immutable update
        this.errorStore.showSuccess('Услуга успешно добавлена.')
      })
      return newDocRef.id // Возвращаем ID новой услуги
    } catch (error) {
      console.error('Ошибка при добавлении услуги:', error)
      this.errorStore.showError('Не удалось добавить услугу.')
      return null
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  async updateService(serviceId, serviceData) {
    const orgId = this.organizationStore.organization.id
    if (!orgId || !serviceId) return

    this.isLoading = true
    try {
      // Заменяем кастомный хелпер `updateDocument` на нативную функцию `updateDoc`
      // из Firebase SDK. Это обеспечивает предсказуемое поведение и соответствует
      // другим частям кодовой базы, устраняя потенциальную причину,
      // по которой поле `photo` не сохранялось в базе данных.
      const serviceRef = doc(db, 'organizations', orgId, 'services', serviceId)
      await updateDoc(serviceRef, {
        ...serviceData,
        updatedAt: serverTimestamp(),
      })

      // Optimistic update
      runInAction(() => {
        const index = this.services.findIndex((s) => s.id === serviceId)
        if (index !== -1) {
          // Иммутабельное обновление: создаем новый массив, заменяя
          // измененный элемент на новый объект.
          this.services = this.services.map((service) =>
            service.id === serviceId ? { ...service, ...serviceData } : service,
          )
        }
        this.errorStore.showSuccess('Услуга успешно обновлена.')
      })
    } catch (error) {
      console.error('Ошибка при обновлении услуги:', error)
      this.errorStore.showError('Не удалось обновить услугу.')
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  setSearchTerm(term) {
    this.searchTerm = term
  }

  async removeService(serviceId) {
    const orgId = this.organizationStore.organization.id
    if (!orgId || !serviceId) return

    this.isLoading = true
    // Находим услугу в локальном состоянии до ее удаления, чтобы получить ее imageUrl
    const serviceToRemove = this.services.find((s) => s.id === serviceId)

    try {
      const serviceDocRef = doc(
        db,
        'organizations',
        orgId,
        'services',
        serviceId,
      )

      // 1. Если у услуги есть изображение, удаляем его из Storage
      if (serviceToRemove?.imageUrl) {
        await deleteImageByUrl(serviceToRemove.imageUrl)
      }

      await deleteDoc(serviceDocRef)

      let servicesToSave
      // Optimistic update and re-ordering
      runInAction(() => {
        const updatedServices = this.services.filter((s) => s.id !== serviceId)
        // Переназначаем 'order' для оставшихся услуг, чтобы не было пробелов
        servicesToSave = updatedServices.map((service, index) => ({
          ...service,
          order: index,
        }))
        this.services = servicesToSave
        this.errorStore.showSuccess('Услуга удалена.')
      })

      // Сохраняем новый порядок на сервере после удаления
      await this.saveOrder(servicesToSave)
    } catch (error) {
      console.error('Ошибка при удалении услуги:', error)
      this.errorStore.showError('Не удалось удалить услугу.')
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  async uploadServicePhoto(serviceId, file) {
    const orgId = this.organizationStore.organization.id
    if (!orgId || !serviceId || !file) return null

    this.isUploading = true
    try {
      // Перед загрузкой нового фото, удаляем старое, если оно есть
      const service = this.services.find((s) => s.id === serviceId)
      if (service?.imageUrl) {
        await deleteImageByUrl(service.imageUrl)
      }
      const photoUrl = await uploadServiceImage(orgId, serviceId, file)
      // Этого вызова достаточно. `updateService` обновит и Firestore,
      // и локальное состояние иммутабельным способом, что вызовет
      // перерисовку компонента.
      await this.updateService(serviceId, { imageUrl: photoUrl })
      return photoUrl
    } catch (error) {
      console.error('Ошибка при загрузке фото услуги:', error)
      this.errorStore.showError('Не удалось загрузить фото.')
      return null
    } finally {
      runInAction(() => (this.isUploading = false))
    }
  }

  get sortedServices() {
    // Теперь, когда мы поддерживаем поле `order`,
    // массив `services` уже отсортирован.
    // Добавляем фильтрацию по категории и поиску.
    let filteredServices = this.services

    if (this.selectedCategory !== 'all') {
      filteredServices = filteredServices.filter(
        (service) => service.category === this.selectedCategory,
      )
    }

    if (this.searchTerm) {
      filteredServices = filteredServices.filter((service) =>
        service.name.toLowerCase().includes(this.searchTerm.toLowerCase()),
      )
    }

    return filteredServices
  }

  get categories() {
    const allCategories = new Set(
      this.services.map((s) => s.category).filter(Boolean),
    )
    // Возвращаем массив с категорией "Все" в начале
    return ['all', ...Array.from(allCategories)]
  }

  selectCategory(category) {
    this.selectedCategory = category
  }

  reorderServices(sourceIndex, destinationIndex) {
    const reordered = Array.from(this.services)
    const [moved] = reordered.splice(sourceIndex, 1)
    reordered.splice(destinationIndex, 0, moved)

    // Обновляем поле 'order' у всех элементов, чтобы оно соответствовало новому порядку в массиве
    const servicesToUpdate = reordered.map((service, index) => ({
      ...service,
      order: index,
    }))

    // Обновляем локальное состояние немедленно для отзывчивости UI
    runInAction(() => {
      this.services = servicesToUpdate
    })

    // Сохраняем новый порядок на сервере
    this.saveOrder(servicesToUpdate)
  }

  get minServiceDuration() {
    if (this.services.length === 0) {
      return 0
    }
    const durations = this.services
      .map((s) => s.duration || 0)
      .filter((d) => d > 0)

    if (durations.length === 0) {
      return 0
    }

    return Math.min(...durations)
  }

  get servicesById() {
    return this.services.reduce((acc, service) => {
      acc[service.id] = service
      return acc
    }, {})
  }

  async saveOrder(orderedServices) {
    const orgId = this.organizationStore.organization.id
    if (!orgId) {
      this.errorStore.showError('Ошибка: не найдена организация.')
      return
    }

    const batch = writeBatch(db)
    orderedServices.forEach((service) => {
      const serviceRef = doc(db, 'organizations', orgId, 'services', service.id)
      batch.update(serviceRef, { order: service.order })
    })

    try {
      await batch.commit()
    } catch (error) {
      console.error('Ошибка при сохранении порядка услуг:', error)
      this.errorStore.showError('Не удалось сохранить порядок услуг.')
    }
  }
}

export default ServiceStore
